import React, { useState, useRef, forwardRef } from 'react';
import _merge from 'lodash/merge';
import { useCombinedRefs, getContext, isAnimationDisabled } from 'common';
import ToggleSwitchLabelContent from './ToggleSwitchLabelContent';

import Styled from './styles';

export interface ToggleSwitchProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /**
   *  Initial state of the ToggleSwitch. Value of true turns ToggleSwitch
   *  on upon initial display.
   */
  checked?: boolean;
  /**
   *  Set to true to use red/green toggle for on/off states.
   */
  stateColor?: boolean;
  /**
   *  Disable the ToggleSwitch.
   */
  disabled?: boolean;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /**
   * Callback function for change in switch value. Returns object of shape ({event, value}):
   *   - event: event:
   *   - value: true or false, the checked state of the ToggleSwitch
   */
  onChange?: (arg: { event: React.MouseEvent | React.KeyboardEvent, value: boolean }) => void;
  /**  Attributes applied to the input element. (excluding { type: 'radio' }). Note: DOM attributes should be camelCased. */
  inputProps?: React.ComponentPropsWithRef<'input'>,
  /**
   * @ignore Callback function for onMouseDown event.
   */
  onMouseDown?: React.MouseEventHandler;
  /**
   * @ignore Callback function for onKeyDown event.
   */
  onKeyDown?: React.KeyboardEventHandler;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

const ToggleSwitch = forwardRef<HTMLElement, ToggleSwitchProps>((props: ToggleSwitchProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const {
    checked,
    onChange,
    disabled,
    onMouseDown,
    onKeyDown,
    inputProps,
    stateColor,
    disableAnimation: propsdisableAnimation,
    theme: propTheme,
    ...restProps
  } = props;
  const wrapperRef = useRef<HTMLElement>();
  const inputRef = useRef();
  const toggleRef = useRef();

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isComponentPressed, setPressed] = useState(false);
  const combinedRef = useCombinedRefs(ref, inputRef);
  const wrapperPosition = useRef({
    x: null,
    y: null,
    width: null,
    height: null,
  });

  const isMousePressed = useRef(false);

  const resolvedTheme = _merge({}, theme.TOGGLE_SWITCH, propTheme);

  const isOverComponent = (e) => {
    const xCoordinate = e.clientX;
    const yCoordinate = e.clientY;
    const { x, y, width, height } = wrapperPosition.current;
    return (
      xCoordinate >= x - 5 &&
      xCoordinate <= (x as number) + (width as number) + 1 &&
      yCoordinate >= y &&
      yCoordinate <= (y as number) + (height as number)
    );
  };

  const updatePosition = () => {
    const rect = wrapperRef.current && wrapperRef.current.getBoundingClientRect();
    wrapperPosition.current = {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    };
  };

  const handleOnChange = (event) => {
    if (onChange && !disabled) {
      onChange({ event, value: !checked });
    }
  };

  const handleMouseDown = (event) => {
    if (!disabled) {
      updatePosition();
      event.stopPropagation();
      isMousePressed.current = true;
      setPressed(true);
      onMouseDown && onMouseDown(event);
    }
  };

  const handleMouseLeave = (event) => {
    event.preventDefault();
    if (!disabled) {
      restProps.onMouseLeave && restProps.onMouseLeave(event);
      isComponentPressed === true && setPressed(false);
    }
  };

  /**
   * if mouse is pressed and cursor is move back to ToggleSwitch,
   * change the press state to be true again.
   */
  const handleMouseEnter = (event) => {
    !disabled && restProps.onMouseEnter && restProps.onMouseEnter(event);
    if (isMousePressed.current === true) {
      setPressed(true);
    }
  };

  const handleKeyDown = (event) => {
    !disabled && onKeyDown && onKeyDown(event);
    if (event.key === 'Enter' || event.key === ' ') {
      handleOnChange(event);
    }
  };

  const handleMouseUp = (event) => {
    isMousePressed.current = false;
    setPressed(false);
    if (isOverComponent(event)) {
      handleOnChange(event);
    }
    inputProps.onMouseUp && inputProps.onMouseUp(event);
  };

  const resolvedInputProps = {
    RTL,
    autoFocus: inputProps.autoFocus,
    checked,
    disabled,
    onChange: (data) => { inputProps.onChange && inputProps.onChange(data); },
    ...inputProps,
    ref: combinedRef,
    onMouseDown: handleMouseDown,
    onKeyDown: handleKeyDown,
    onMouseUp: handleMouseUp,
    onMouseLeave: (data) => { setIsHovered(false); inputProps.onMouseLeave && inputProps.onMouseLeave(data); },
    onMouseEnter: (data) => {
      setIsHovered(true);
      inputProps.onMouseEnter && inputProps.onMouseEnter(data);
    },
    onClick: (data) => { toggleRef && toggleRef.current && (toggleRef.current as HTMLElement).focus(); inputProps.onClick && inputProps.onClick(data); },
    onFocus: (data) => { setIsFocused(true); inputProps.onFocus && inputProps.onFocus(data); },
    onBlur: (data) => { setIsFocused(false); inputProps.onBlur && inputProps.onBlur(data); },
  };

  // clicking on track should toggle the switch
  const handleTrackClick = (event) => {
    handleOnChange(event);
  };

  return (
    <Styled.ToggleSwitchWrapper
      aria-disabled={disabled}
      aria-checked={checked}
      ref={wrapperRef}
      theme={resolvedTheme}
      disabled={disabled}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      {...restProps}
    >
      <Styled.Track
        aria-hidden="true"
        checked={checked}
        stateColor={stateColor}
        theme={resolvedTheme}
        disabled={disabled}
        onClick={handleTrackClick}
      />
      <Styled.Toggle
        ref={toggleRef}
        isFocused={isFocused}
        isHovered={isHovered}
        disabled={disabled}
        pressed={isComponentPressed}
        checked={checked}
        stateColor={stateColor}
        RTL={RTL}
        theme={resolvedTheme}
        $disableAnimation={isAnimationDisabled(propsdisableAnimation, disableAnimation)}
      />
      <Styled.Input {...resolvedInputProps} />

    </Styled.ToggleSwitchWrapper>
  );
}));

ToggleSwitch.defaultProps = {
  checked: false,
  stateColor: false,
  disabled: false,
  disableAnimation: undefined,
  onChange: undefined,
  onMouseDown: undefined,
  inputProps: {},
  onKeyDown: undefined,
  theme: {}
};

ToggleSwitch.displayName = 'ToggleSwitch';

export {
  ToggleSwitch as default,
  ToggleSwitchLabelContent,
};
